import React, { useState, useEffect } from 'react';
import './Dashboard.css';

interface BackupHistoryItem {
  backup_date: string;
  backup_timestamp: string;
  status: string;
  total_employees: number;
  total_leads: number;
  total_invoices: number;
  employee_files_count: number;
  folder_path: string;
  duration_ms: number;
}

interface BackupFilesResponse {
  date: string;
  master_files: string[];
  employee_files: string[];
}

const BackupManagement: React.FC = () => {
  const [history, setHistory] = useState<BackupHistoryItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [runningManual, setRunningManual] = useState<boolean>(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [dateFiles, setDateFiles] = useState<BackupFilesResponse | null>(null);
  const [loadingFiles, setLoadingFiles] = useState<boolean>(false);
  const [msg, setMsg] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  const getApiUrl = () => {
    if (process.env.REACT_APP_API_URL) {
      return process.env.REACT_APP_API_URL.replace(/\/api\/?$/, '');
    }
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      const protocol = window.location.protocol;
      if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.startsWith('192.168.') || hostname.startsWith('10.')) {
        return `${protocol}//${hostname}:5000`;
      }
    }
    return 'http://200.97.170.56:5000';
  };

  const API_BASE = getApiUrl();

  const fetchBackups = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/backups`);
      if (res.ok) {
        const data = await res.json();
        if (data.success && Array.isArray(data.history)) {
          setHistory(data.history);
          if (data.history.length > 0 && !selectedDate) {
            handleViewFiles(data.history[0].backup_date);
          }
        }
      }
    } catch (e: any) {
      console.error('Error fetching backups:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBackups();
  }, []);

  const handleRunBackupNow = async () => {
    if (!window.confirm('Kya aap abhi turant naya Leads & Accounts Backup generate karna chahte hain?')) return;
    setRunningManual(true);
    setMsg(null);
    try {
      const res = await fetch(`${API_BASE}/api/backups/run-now`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        setMsg({ text: '✅ Backup successfully generate ho gaya!', type: 'success' });
        await fetchBackups();
        if (data.data?.backup_date) {
          handleViewFiles(data.data.backup_date);
        }
      } else {
        setMsg({ text: `❌ Backup error: ${data.message || data.error}`, type: 'error' });
      }
    } catch (e: any) {
      setMsg({ text: `❌ Network error: ${e.message}`, type: 'error' });
    } finally {
      setRunningManual(false);
    }
  };

  const handleViewFiles = async (date: string) => {
    setSelectedDate(date);
    setLoadingFiles(true);
    try {
      const res = await fetch(`${API_BASE}/api/backups/files/${date}`);
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setDateFiles(data);
        }
      }
    } catch (e) {
      console.error('Error loading files for date:', e);
    } finally {
      setLoadingFiles(false);
    }
  };

  const getDownloadUrl = (date: string, filename: string) => {
    return `${API_BASE}/api/backups/download/${date}/${encodeURIComponent(filename)}`;
  };

  const latestStats = history.length > 0 ? history[0] : null;

  return (
    <div style={{ padding: '0.5rem 0.75rem', maxWidth: '100%', margin: '0 auto', fontSize: '13px' }}>
      
      {/* Ultra-compact single line header bar */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        background: '#ffffff',
        padding: '0.4rem 0.75rem',
        borderRadius: '8px',
        border: '1px solid #E2E8F0',
        boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
        marginBottom: '0.4rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '1.1rem' }}>🛡️</span>
          <span style={{ fontWeight: 700, fontSize: '0.92rem', color: '#0F172A' }}>
            Daily Leads & Accounts Backup
          </span>
          <span style={{
            fontSize: '0.68rem',
            fontWeight: 700,
            padding: '0.15rem 0.45rem',
            borderRadius: '999px',
            background: '#ECFDF5',
            color: '#047857',
            border: '1px solid #A7F3D0'
          }}>
            Auto: 12:00 AM IST
          </span>
        </div>

        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
          <button
            onClick={fetchBackups}
            style={{
              padding: '0.25rem 0.55rem',
              background: '#F1F5F9',
              border: '1px solid #CBD5E1',
              borderRadius: '5px',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '0.74rem',
              color: '#334155'
            }}
          >
            🔄 Refresh
          </button>
          
          <button
            onClick={handleRunBackupNow}
            disabled={runningManual}
            style={{
              padding: '0.25rem 0.7rem',
              background: runningManual ? '#94A3B8' : '#00796B',
              color: '#ffffff',
              border: 'none',
              borderRadius: '5px',
              cursor: runningManual ? 'not-allowed' : 'pointer',
              fontWeight: 600,
              fontSize: '0.74rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.3rem'
            }}
          >
            {runningManual ? '⏳ Running...' : '⚡ Run Backup Now'}
          </button>
        </div>
      </div>

      {/* Alert Notification */}
      {msg && (
        <div style={{
          padding: '0.35rem 0.65rem',
          borderRadius: '6px',
          marginBottom: '0.4rem',
          background: msg.type === 'success' ? '#ECFDF5' : '#FEF2F2',
          border: `1px solid ${msg.type === 'success' ? '#A7F3D0' : '#FECACA'}`,
          color: msg.type === 'success' ? '#065F46' : '#991B1B',
          fontWeight: 600,
          fontSize: '0.75rem'
        }}>
          {msg.text}
        </div>
      )}

      {/* Ultra-compact horizontal info bar (Replaces 4 huge cards) */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '0.8rem',
        padding: '0.35rem 0.75rem',
        background: '#F8FAFC',
        border: '1px solid #E2E8F0',
        borderRadius: '6px',
        marginBottom: '0.5rem',
        fontSize: '0.74rem',
        color: '#475569'
      }}>
        <div><strong style={{ color: '#0F172A' }}>Schedule:</strong> 12:00 AM IST (Daily Cron)</div>
        <span style={{ color: '#CBD5E1' }}>|</span>
        <div><strong style={{ color: '#0F172A' }}>Latest Leads:</strong> <span style={{ color: '#00796B', fontWeight: 700 }}>{latestStats ? latestStats.total_leads.toLocaleString('en-IN') : '0'}</span></div>
        <span style={{ color: '#CBD5E1' }}>|</span>
        <div><strong style={{ color: '#0F172A' }}>Employees Covered:</strong> <span style={{ color: '#00796B', fontWeight: 700 }}>{latestStats ? latestStats.total_employees : '0'}</span></div>
        <span style={{ color: '#CBD5E1' }}>|</span>
        <div><strong style={{ color: '#0F172A' }}>Server Storage:</strong> 200.97.170.56 (/public/backups/)</div>
        <span style={{ color: '#CBD5E1' }}>|</span>
        <div><strong style={{ color: '#0F172A' }}>Retention:</strong> 30 Days Rolling</div>
      </div>

      {/* Main 2-Column Dense Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '230px 1fr', gap: '0.5rem' }}>
        
        {/* Left Column: Compact Dates List */}
        <div style={{
          background: '#ffffff',
          borderRadius: '8px',
          border: '1px solid #E2E8F0',
          padding: '0.5rem',
          height: 'fit-content'
        }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', marginBottom: '0.35rem', letterSpacing: '0.04em' }}>
            📅 Backup Dates
          </div>

          {loading ? (
            <div style={{ padding: '0.75rem', textAlign: 'center', color: '#64748B', fontSize: '0.75rem' }}>Loading...</div>
          ) : history.length === 0 ? (
            <div style={{ padding: '0.75rem', textAlign: 'center', color: '#64748B', fontSize: '0.75rem' }}>Koi backup nahi mila.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
              {history.map((item) => {
                const isSelected = selectedDate === item.backup_date;
                return (
                  <div
                    key={item.backup_date}
                    onClick={() => handleViewFiles(item.backup_date)}
                    style={{
                      padding: '0.35rem 0.5rem',
                      borderRadius: '5px',
                      border: isSelected ? '1.5px solid #00796B' : '1px solid #E2E8F0',
                      background: isSelected ? '#F0FDF4' : '#FAFAFA',
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 700, color: '#0F172A', fontSize: '0.78rem' }}>
                        {item.backup_date}
                      </div>
                      <div style={{ fontSize: '0.68rem', color: '#64748B' }}>
                        {item.total_leads} leads • {item.duration_ms}ms
                      </div>
                    </div>
                    <span style={{
                      padding: '0.1rem 0.35rem',
                      borderRadius: '4px',
                      fontSize: '0.65rem',
                      fontWeight: 700,
                      background: item.status === 'Success' ? '#DCFCE7' : '#FEE2E2',
                      color: item.status === 'Success' ? '#166534' : '#991B1B'
                    }}>
                      {item.status}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Right Column: Compact Files & 2-Col Employee Grid */}
        <div style={{
          background: '#ffffff',
          borderRadius: '8px',
          border: '1px solid #E2E8F0',
          padding: '0.6rem'
        }}>
          
          {loadingFiles ? (
            <div style={{ padding: '1rem', textAlign: 'center', color: '#64748B', fontSize: '0.75rem' }}>Files load ho rahi hain...</div>
          ) : !dateFiles ? (
            <div style={{ padding: '1rem', textAlign: 'center', color: '#64748B', fontSize: '0.75rem' }}>Date select karein</div>
          ) : (
            <div>
              {/* Master files horizontal bar */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '0.5rem',
                padding: '0.4rem 0.6rem',
                background: '#F8FAFC',
                borderRadius: '6px',
                border: '1px solid #E2E8F0',
                marginBottom: '0.5rem'
              }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#0F172A' }}>
                  📊 Master Files: <span style={{ color: '#00796B' }}>{dateFiles.date}</span>
                </div>
                <div style={{ display: 'flex', gap: '0.4rem' }}>
                  {dateFiles.master_files.map((file) => (
                    <a
                      key={file}
                      href={getDownloadUrl(dateFiles.date, file)}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        padding: '0.2rem 0.55rem',
                        background: file.endsWith('.csv') ? '#00796B' : '#475569',
                        color: '#ffffff',
                        borderRadius: '4px',
                        textDecoration: 'none',
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.25rem'
                      }}
                    >
                      ⬇ {file.endsWith('.csv') ? 'Master CSV (All Leads)' : 'Master JSON'}
                    </a>
                  ))}
                </div>
              </div>

              {/* Employee-Wise Section in 2/3-Column Dense Grid */}
              <div>
                <div style={{
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  color: '#64748B',
                  textTransform: 'uppercase',
                  marginBottom: '0.35rem',
                  letterSpacing: '0.04em'
                }}>
                  👥 Employee-Wise Backups ({dateFiles.employee_files.filter(f => f.endsWith('.csv')).length} Employees)
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                  gap: '0.35rem',
                  maxHeight: '440px',
                  overflowY: 'auto',
                  paddingRight: '0.2rem'
                }}>
                  {dateFiles.employee_files.filter(f => f.endsWith('.csv')).map((file) => {
                    const cleanTitle = file.replace(/_leads_accounts\.csv$/, '').replace(/_/g, ' ');
                    return (
                      <div
                        key={file}
                        style={{
                          padding: '0.3rem 0.5rem',
                          background: '#FAFAFA',
                          border: '1px solid #E2E8F0',
                          borderRadius: '5px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.35rem',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          textOverflow: 'ellipsis'
                        }}>
                          <span style={{ fontSize: '0.85rem' }}>👤</span>
                          <span style={{
                            fontWeight: 600,
                            fontSize: '0.73rem',
                            color: '#1E293B',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>
                            {cleanTitle}
                          </span>
                        </div>
                        <a
                          href={getDownloadUrl(dateFiles.date, file)}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            padding: '0.15rem 0.45rem',
                            background: '#0F172A',
                            color: '#ffffff',
                            borderRadius: '3px',
                            textDecoration: 'none',
                            fontWeight: 600,
                            fontSize: '0.66rem',
                            flexShrink: 0
                          }}
                        >
                          ⬇ CSV
                        </a>
                      </div>
                    );
                  })}
                </div>
              </div>

            </div>
          )}

        </div>

      </div>

    </div>
  );
};

export default BackupManagement;
